import React, { useState, useRef } from 'react';
import { Input, Button, Space, message, Form as AntForm, Tooltip, Switch, Typography, Select, InputNumber } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { MODULE_IDS } from '../../utils/constants';
import { formatQty } from '../../utils/formatters';

import { getAllRawMaterials, createRawMaterial, updateRawMaterial, deleteRawMaterial } from '../../api/rawMaterials.api';
import { getUnits } from '../../api/units.api'; 

import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import FormDrawer from '../../components/common/FormDrawer';
import ConfirmDelete from '../../components/common/ConfirmDelete';
import StockBadge from '../../components/common/StockBadge';

const { Text } = Typography;

const RawMaterialPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRaw, setEditingRaw] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false); 
  
  const [tableParams, setTableParams] = useState({ current: 1, pageSize: 50 });
  const searchRef = useRef(null);

  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();

  const { canAdd, canEdit, canDelete } = usePermissions(MODULE_IDS.RAW_MATERIAL);

  const { control, handleSubmit, reset, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      rawName: '',
      unitID: null,
      openingQty: 0,
      hsnCode: ''
    }
  });

  const { data, isLoading: isFetching } = useQuery({
    queryKey: ['rawMaterials', plantId, searchText, tableParams.current, tableParams.pageSize, lowStockOnly],
    queryFn: () => getAllRawMaterials(plantId, { 
      search: searchText, 
      page: tableParams.current, 
      pageSize: tableParams.pageSize,
      lowStock: lowStockOnly ? true : undefined
    }),
    enabled: !!plantId,
  });

  const rawMaterials = data?.items || [];
  const totalCount = data?.total || 0;


  const { data: unitsData = [], isLoading: isFetchingUnits } = useQuery({
    queryKey: ['units', plantId],
    queryFn: () => getUnits(plantId),
    enabled: !!plantId,
  });

  
  const createMutation = useMutation({
    mutationFn: (data) => createRawMaterial(plantId, data),
    onSuccess: () => {
      message.success('Raw Material created successfully!');
      queryClient.invalidateQueries({ queryKey: ['rawMaterials'] });
      closeDrawer();
    },
    onError: (err) => {
      message.error(err.message.includes('exists') ? 'Raw Material already exists.' : err.message);
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data) => updateRawMaterial(plantId, editingRaw.rawID, data),
    onSuccess: () => {
      message.success('Raw Material updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['rawMaterials'] });
      closeDrawer();
    },
    onError: (err) => {
      message.error(err.message.includes('exists') ? 'Raw Material already exists.' : err.message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (rawId) => deleteRawMaterial(plantId, rawId),
    onSuccess: () => {
      message.success('Raw Material deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['rawMaterials'] });
    },
    onError: (err) => {
      message.error(err.message || 'Cannot delete raw material. It is in use.');
    }
  });

  
  const openDrawer = (record = null) => {
    setEditingRaw(record);
    if (record) {
      reset({
        rawName: record.rawName,
        unitID: record.unitID,
        openingQty: record.openingQty,
        hsnCode: record.hsnCode || ''
      });
    } else {
      reset({ rawName: '', unitID: null, openingQty: 0, hsnCode: '' });
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingRaw(null);
    reset();
  };

  const onSubmit = (formData) => {
    if (editingRaw) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleTableChange = (pagination) => {
    setTableParams({ current: pagination.current, pageSize: pagination.pageSize });
  };

  const handleSearch = (val) => {
    setSearchText(val);
    setTableParams(prev => ({ ...prev, current: 1 }));
  };

  // Keyboard shortcuts
  useKeyboardShortcuts([
    { key: 'n', ctrl: true, action: () => canAdd && openDrawer(null), description: 'Add new raw material' },
    { key: 'f', ctrl: true, action: () => searchRef.current?.focus(), description: 'Focus search' },
    { key: 'r', ctrl: true, action: () => queryClient.invalidateQueries({ queryKey: ['rawMaterials'] }), description: 'Refresh data' },
    { key: 's', ctrl: true, action: () => drawerOpen && handleSubmit(onSubmit)(), description: 'Save form' },
    { key: 'Escape', action: () => drawerOpen && closeDrawer(), description: 'Close drawer' },
  ]);

  
  const columns = [
    { title: '#', dataIndex: 'index', render: (text, record, index) => (tableParams.current - 1) * tableParams.pageSize + index + 1, width: 60 },
    { title: 'Raw Name', dataIndex: 'rawName', key: 'rawName' },
    { title: 'Unit', dataIndex: 'unitDisplayName', key: 'unitDisplayName' },
    { title: 'Opening Qty', dataIndex: 'openingQty', key: 'openingQty', render: (val) => formatQty(val) },
    { title: 'Purchase Qty', dataIndex: 'purchaseQty', key: 'purchaseQty', render: (val) => formatQty(val) },
    { title: 'Consumption Qty', dataIndex: 'consumptionQty', key: 'consumptionQty', render: (val) => formatQty(val) },
    { 
      title: 'Balance Qty', 
      key: 'balanceQty', 
      render: (_, record) => (
        <Space>
          <Text strong>{formatQty(record.balanceQty)}</Text>
          <StockBadge qty={record.balanceQty} threshold={10} />
        </Space>
      ) 
    },
    { title: 'HSN Code', dataIndex: 'hsnCode', key: 'hsnCode' },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          {canEdit && (
            <Tooltip title="Edit (Enter)">
              <Button type="text" icon={<EditOutlined style={{ color: '#1890ff' }} />} onClick={() => openDrawer(record)} />
            </Tooltip>
          )}
          {canDelete && (
            <ConfirmDelete 
              itemName={record.rawName} 
              onConfirm={() => deleteMutation.mutate(record.rawID)}
              loading={deleteMutation.isPending && deleteMutation.variables === record.rawID}
            />
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader 
        title="Raw Material Master" 
        subtitle="Manage inventory items, opening stock, and HSN codes"
        onAdd={() => openDrawer(null)} 
        canAdd={canAdd} 
        addLabel="Add Raw Material"
        extras={
          <Space>
            <Text>Low Stock Only</Text>
            <Switch checked={lowStockOnly} onChange={(checked) => {
              setLowStockOnly(checked);
              setTableParams(prev => ({ ...prev, current: 1 }));
            }} />
          </Space>
        }
      />

      <div style={{ marginBottom: 16, maxWidth: 400 }}>
        <Input.Search 
          ref={searchRef}
          placeholder="Search raw material... (Ctrl+F)" 
          allowClear 
          onSearch={handleSearch}
        />
      </div>

      <DataTable 
        columns={columns} 
        data={rawMaterials} 
        loading={isFetching} 
        rowKey="rawID"
        pagination={{
          current: tableParams.current,
          pageSize: tableParams.pageSize,
          total: totalCount
        }}
        onChange={handleTableChange}
      />

      <FormDrawer
        title={editingRaw ? "Edit Raw Material" : "Add Raw Material"}
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={handleSubmit(onSubmit)}
        loading={createMutation.isPending || updateMutation.isPending}
        isDirty={isDirty}
        width={450}
      >
        <AntForm layout="vertical">
          <AntForm.Item label="Raw Material Name" validateStatus={errors.rawName ? 'error' : ''} help={errors.rawName?.message} required>
            <Controller
              name="rawName"
              control={control}
              rules={{ required: 'Name is required', maxLength: { value: 100, message: 'Max 100 characters' } }}
              render={({ field }) => <Input {...field} placeholder="e.g. Empty Plastic Bottle 1L" />}
            />
          </AntForm.Item>

          <AntForm.Item label="Unit" validateStatus={errors.unitID ? 'error' : ''} help={errors.unitID?.message} required>
            <Controller
              name="unitID"
              control={control}
              rules={{ required: 'Unit is required' }}
              render={({ field }) => (
                <Select {...field} placeholder="Select Unit" loading={isFetchingUnits} showSearch filterOption={(input, option) => {
                  if (!option || !input) return true;
                  const label = typeof option.label === 'string' ? option.label : String(option.label || '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }} options={unitsData.map(u => ({
                  value: u.unitID,
                  label: `${u.purchaseUnit || ''} / ${u.consumptionUnit || ''}`
                }))} />
              )}
            />
          </AntForm.Item>

          <AntForm.Item label="Opening Qty" validateStatus={errors.openingQty ? 'error' : ''} help={errors.openingQty?.message}>
            <Controller
              name="openingQty"
              control={control}
              rules={{ min: { value: 0, message: 'Cannot be negative' } }}
              render={({ field }) => <InputNumber {...field} style={{ width: '100%' }} min={0} />}
            />
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
              * Opening quantity is set once. Stock will update automatically from purchases.
            </Text>
          </AntForm.Item>

          <AntForm.Item label="HSN Code" validateStatus={errors.hsnCode ? 'error' : ''} help={errors.hsnCode?.message}>
            <Controller
              name="hsnCode"
              control={control}
              rules={{ maxLength: { value: 20, message: 'Max 20 characters' } }}
              render={({ field }) => <Input {...field} placeholder="e.g. 39233090" />}
            />
          </AntForm.Item>
        </AntForm>
      </FormDrawer>
    </div>
  );
};

export default RawMaterialPage;